import React, {createContext, useState, useEffect, ReactNode} from 'react';

import Formats from '../../common/utils/Formats';
import LocResourcesService from '../services/LocResourcesService';
import { LocResourcesData } from '../utils/types';
import MetricsService from "../services/MetricsService";
import {Locale, SiteId} from "@zooplus/commons-lang";

export interface LocResourcesContextProps {
  onFormatLocale: (localeKey: string, params?: any) => string;
  onFormatDate: (date: Date) => string;
  locResources: LocResourcesData[] | null;
  onFormatPrice: (amount: number, currency: string) => string;
  onFormatDateString: (date: Date) => string;
}

const LocResourcesContext = createContext<Partial<LocResourcesContextProps>>({});

export interface LocResourcesProviderProps {
  children: ReactNode;
  locale: Locale;
  siteId: SiteId;
  keys: string[];
  metricsService: MetricsService;
}

export const LocResourcesProvider: React.FC<LocResourcesProviderProps> = (
  { children, locale, siteId, keys, metricsService }: LocResourcesProviderProps) => {
  const [locResources, setLocResources] = useState<LocResourcesData[] | null>(null);
  const formats = Formats({ locResources, locale });

  const locResourcesService = new LocResourcesService(metricsService);

  const updateLocResources = (): void => {
    locResourcesService
      .onGetLocResources(siteId, locale, keys)
      .then(setLocResources);
  };

  useEffect(updateLocResources, []);

  const onFormatDate = (date: Date): string => {
    return formats.formatDate(date);
  };

  const onFormatLocale = (localeKey: string, params: any = undefined): string => {
    return formats.locale(localeKey, params);
  };

  const onFormatPrice = (amount: number, currency: string): string => {
    return formats.formatPrice(amount, currency);
  };

  const onFormatDateString = (date: Date): string => {
    return formats.formatDateString(date);
  };

  return (
    <LocResourcesContext.Provider
      value={{ onFormatLocale, onFormatDate, locResources, onFormatPrice, onFormatDateString }}>
      {children}
    </LocResourcesContext.Provider>
  );
};

export default LocResourcesContext;
